// components/UserSearchSelectEnhanced.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Spin, Tag } from 'antd';
import debounce from 'lodash/debounce';
import userService from '../../services/userService';
import userMainService from '../../services/user';

const UserSearchSelectEnhanced = ({
    mode = 'multiple',
    value = mode === 'multiple' ? [] : undefined,
    onChange,
    placeholder = 'Select Users',
    size = 'large',
    allowClear = true,
    style = { width: '100%' },
    disabled = false,
    showEmail = true,
    maxTagCount = 'responsive',
    initialUsers = [],
    ...restProps
}) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    
    // Track loading status in a ref to avoid stale closure issues during debounced calls
    const loadingRef = useRef(false);

    const fetchUsers = async (pageNum = 1, search = '', append = false) => {
        if (loadingRef.current) return;
        
        loadingRef.current = true;
        setLoading(true);
        try {
            if (search.trim() !== '') {
                // Search endpoint
                const response = await userService.searchUsers(search);
                if (response) {
                    const newUsers = Array.isArray(response) ? response : response.data || response.items || [];
                    setUsers(prev => append ? [...prev, ...newUsers] : newUsers);
                    setHasMore(false); // Search typically doesn't paginate in this setup
                    setPage(1);
                }
            } else {
                // Paginated initial load endpoint
                const response = await userMainService.getUsersPaginated(pageNum, 10, '');
                if (response) {
                    const newUsers = Array.isArray(response) ? response : response.data || response.items || [];
                    const meta = response.meta || response || {};
                    
                    setUsers(prev => (append && pageNum > 1) ? [...prev, ...newUsers] : newUsers);
                    
                    const total = meta.total || newUsers.length;
                    const currentPage = meta.current_page || pageNum;
                    const perPage = meta.per_page || 10;
                    
                    setHasMore(currentPage * perPage < total);
                    setPage(currentPage);
                }
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    };

    // Keep the latest fetch function in a ref so debounce always uses the current state context
    const fetchUsersRef = useRef(fetchUsers);
    useEffect(() => {
        fetchUsersRef.current = fetchUsers;
    }, [fetchUsers]);

    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            setPage(1);
            fetchUsersRef.current(1, searchValue, false);
        }, 300),
        []
    );

    const handleSearch = (searchValue) => {
        setSearchTerm(searchValue);
        debouncedSearch(searchValue);
    };

    const handlePopupScroll = (e) => {
        const { target } = e;
        const isAtBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 10;
        
        if (isAtBottom && hasMore && !loadingRef.current) {
            fetchUsers(page + 1, searchTerm, true);
        }
    };

    // Initial load and cleanup
    useEffect(() => {
        fetchUsers(1, '');
        return () => {
            debouncedSearch.cancel();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Merge existing users with new searches dynamically
    const allUsersMap = new Map();
    [...initialUsers, ...users].forEach(u => {
        if (u && u.id) allUsersMap.set(u.id, u);
    });
    const allUsers = Array.from(allUsersMap.values());

    const selectOptions = allUsers.map(user => ({
        label: (
            <div style={{ display: 'inline-block', lineHeight: 'normal', verticalAlign: 'middle', paddingTop: '4px', paddingBottom: '4px' }}>
                <div style={{ lineHeight: '1.2' }}>{user.name}</div>
                {showEmail && user.email && (
                    <div style={{ fontSize: '12px', color: '#999', lineHeight: '1.2', marginTop: '2px' }}>
                        {user.email}
                    </div>
                )}
            </div>
        ),
        value: user.id
    }));

    return (
        <Select
            mode={mode}
            value={value}
            onChange={onChange}
            onSearch={handleSearch}
            onPopupScroll={handlePopupScroll}
            placeholder={placeholder}
            size={size}
            allowClear={allowClear}
            style={style}
            loading={loading}
            disabled={disabled}
            filterOption={false}
            notFoundContent={loading ? <Spin size="small" /> : null}
            showSearch
            maxTagCount={maxTagCount}
            maxTagPlaceholder={(omittedValues) => (
                <Tag style={{ marginRight: 3 }}>
                    +{omittedValues.length} users
                </Tag>
            )}
            styles={{
                popup: {
                    maxHeight: '400px',
                    ...(restProps.dropdownStyle || {})
                }
            }}
            options={selectOptions}
            {...restProps}
        />
    );
};

export default UserSearchSelectEnhanced;